import {
  Checkbox,
  Icon,
  Link,
  Panel,
  PanelType,
  Stack,
} from "@fluentui/react";
import React from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface ColumnToggleDef {
  key: string;
  labelKey: string;
}

interface Props {
  isOpen: boolean;
  onDismiss: () => void;
  columns: ColumnToggleDef[];
  visibility: Record<string, boolean>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  onReorder: (sourceIndex: number, destIndex: number) => void;
}

export const ColumnVisibilityPanel: React.FC<Props> = ({
  isOpen,
  onDismiss,
  columns,
  visibility,
  onToggle,
  onShowAll,
  onReorder,
}) => {
  const { t } = useTranslation();

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      headerText={t("ram.columnVisibility.title")}
      type={PanelType.smallFixedFar}
      isLightDismiss
    >
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 16 } }}>
        <ShowAllRow>
          <Link onClick={onShowAll}>{t("ram.columnVisibility.showAll")}</Link>
        </ShowAllRow>
        <Divider />
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="column-visibility">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {columns.map((col, index) => (
                  <Draggable
                    key={col.key}
                    draggableId={col.key}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <DraggableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        isDragging={snapshot.isDragging}
                      >
                        <DragHandle {...provided.dragHandleProps}>
                          <Icon iconName="GripperDotsVertical" />
                        </DragHandle>
                        <Checkbox
                          label={t(col.labelKey)}
                          checked={visibility[col.key] !== false}
                          onChange={() => onToggle(col.key)}
                        />
                      </DraggableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Stack>
    </Panel>
  );
};

const ShowAllRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #edebe9;
  margin: 4px 0;
`;

const DraggableRow = styled.div<{ isDragging: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 0;
  background: ${({ isDragging }) => (isDragging ? "#f3f2f1" : "transparent")};
  border-radius: 2px;
  user-select: none;
`;

const DragHandle = styled.span`
  display: flex;
  align-items: center;
  margin-right: 8px;
  cursor: grab;
  color: #605e5c;
  &:active {
    cursor: grabbing;
  }
`;
